import PatientNoteDataCRUD from './patientNoteData.api'

/**
 * Batch utility for loading patient notes for multiple patients
 * Prevents N+1 request patterns by batching requests and using shared cache
 * 
 * Example:
 *   const patientIds = ['123', '456', '789']
 *   const results = await batchGetPatientNotes(patientIds, { concurrency: 5 })
 *   // Returns: Map<patientId, noteData>
 */

const DEFAULT_BATCH_CONCURRENCY = 5

/**
 * Load notes for multiple patients with controlled concurrency
 * @param {string[]} patientIds - Array of patient IDs to fetch notes for
 * @param {Object} options - Configuration options
 * @param {number} options.concurrency - Max concurrent requests (default: 5)
 * @param {boolean} options.skipErrors - Continue on individual patient errors (default: true)
 * @returns {Promise<Map>} Map of patientId -> noteData
 */
export async function batchGetPatientNotes(patientIds, options = {}) {
    const { 
        concurrency = DEFAULT_BATCH_CONCURRENCY,
        skipErrors = true 
    } = options

    if (!Array.isArray(patientIds) || patientIds.length === 0) {
        return new Map()
    }

    // Remove duplicates and invalid IDs
    const uniqueIds = [...new Set(patientIds.map(id => String(id || '').trim()).filter(Boolean))]

    const results = new Map()
    const errors = new Map()

    // Process in chunks to respect concurrency limit
    for (let i = 0; i < uniqueIds.length; i += concurrency) {
        const chunk = uniqueIds.slice(i, i + concurrency)
        
        const promises = chunk.map(async (patientId) => {
            try {
                const data = await PatientNoteDataCRUD('GET', patientId)
                results.set(patientId, data)
            } catch (error) {
                errors.set(patientId, error)
                if (!skipErrors) {
                    throw error
                }
            }
        })

        await Promise.all(promises)
    }

    // Log errors if any and skipErrors is false
    if (errors.size > 0) {
        console.warn(`Batch patient notes fetch: ${errors.size}/${uniqueIds.length} failed`, {
            failedIds: Array.from(errors.keys()),
            errors: Object.fromEntries(errors)
        })
    }

    return results
}

/**
 * Load notes for multiple patients and merge into single structure
 * Useful when you need to look up notes across multiple patients
 * 
 * @param {string[]} patientIds - Patient IDs to fetch
 * @param {Object} options - Batch options
 * @returns {Promise<Object>} Merged structure: { [patientId]: noteData }
 */
export async function getPatientNotesMap(patientIds, options = {}) {
    const resultMap = await batchGetPatientNotes(patientIds, options)
    const merged = {}
    
    for (const [patientId, noteData] of resultMap.entries()) {
        merged[patientId] = noteData
    }
    
    return merged
}

/**
 * Find specific note across multiple patients
 * @param {string[]} patientIds - Patient IDs to search
 * @param {string} noteId - Note ID to find
 * @returns {Promise<Object|null>} Note data or null if not found
 */
export async function findNoteAcrossPatients(patientIds, noteId) {
    const notesMap = await getPatientNotesMap(patientIds)
    
    for (const [, noteData] of Object.entries(notesMap)) {
        try {
            const parsed = typeof noteData === 'string' ? JSON.parse(noteData) : noteData
            const data = parsed?.d ? JSON.parse(parsed.d) : parsed
            const notes = data?.Data || []
            
            const found = notes.find(note => String(note?.id) === String(noteId))
            if (found) return found
        } catch (e) {
            // Skip parse errors and continue
            continue
        }
    }
    
    return null
}
